import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { supplierAPI } from '../../api/suppliers';
import { CreateSupplierRequest } from '../../types/api';
import toast from 'react-hot-toast';

export const SupplierForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateSupplierRequest>({
    defaultValues: {
      status: 'ACTIVE',
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      loadSupplier(parseInt(id));
    }
  }, [id, isEdit]);

  const loadSupplier = async (supplierId: number) => {
    try {
      const supplier = await supplierAPI.getById(supplierId);
      reset({
        name: supplier.name,
        phone: supplier.phone || '',
        address: supplier.address || '',
        contactPerson: supplier.contactPerson || '',
        status: supplier.status || 'ACTIVE',
      });
    } catch (error) {
      toast.error(t('suppliers.loadError'));
      navigate('/suppliers');
    }
  };

  const onSubmit = async (data: CreateSupplierRequest) => {
    setLoading(true);
    try {
      if (isEdit && id) {
        await supplierAPI.update(parseInt(id), data);
        toast.success(t('suppliers.updateSuccess'));
      } else {
        await supplierAPI.create(data);
        toast.success(t('suppliers.createSuccess'));
      }
      navigate('/suppliers');
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">
        {isEdit ? t('suppliers.editSupplier') : t('suppliers.addNew')}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('suppliers.name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('name', { required: t('validation.required') })}
            className="input-field"
            placeholder={t('suppliers.name')}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('suppliers.phone')}</label>
          <input
            type="text"
            {...register('phone')}
            className="input-field"
            placeholder={t('suppliers.phone')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('suppliers.contactPerson')}</label>
          <input
            type="text"
            {...register('contactPerson')}
            className="input-field"
            placeholder={t('suppliers.contactPersonPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('suppliers.address')}</label>
          <textarea
            {...register('address')}
            className="input-field"
            rows={3}
            placeholder={t('suppliers.address')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('common.status')}</label>
          <select {...register('status')} className="input-field">
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="INACTIVE">{t('common.inactive')}</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/suppliers')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? t('common.loading') : isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
    </div>
  );
};
